import type { ReactElement } from "react";
import { ClassNames } from "@emotion/react";
import { Banner, Flex, Text } from "@jsq/ds/core";
import type { MappingValidationBannerVariant } from "~/graphql/supergraphTypes";

const INVALID_MAPPING_TITLE = "Invalid mapping";

const SINGLE_INVALID_MAPPING_ERROR = `There is an issue with the transaction mapping defined for {arenaName}.
In order to activate the GL integration for the entities in {arenaName},
you will need to update the mapping.`;

const MULTIPLE_INVALID_MAPPING_ERROR = `There is an issue with the transaction mapping defined for one or more entities in {arenaName}.
In order to activate the GL integration for these entities in {arenaName},
you will need to update the following mappings:`;

type InvalidMappingBannerProps = {
  arenaName: string;
  mappingsLink: string;
  variant: MappingValidationBannerVariant;
  invalidMappingNames: string[];
};

export const InvalidMappingBanner = ({
  arenaName,
  mappingsLink,
  variant,
  invalidMappingNames,
}: InvalidMappingBannerProps): ReactElement => {
  const isMultiple = variant === "multiple";
  const descriptionText = (
    isMultiple ? MULTIPLE_INVALID_MAPPING_ERROR : SINGLE_INVALID_MAPPING_ERROR
  ).replaceAll("{arenaName}", arenaName);

  const description = isMultiple ? (
    <Flex flexDirection="column" gap={4}>
      <Text>{descriptionText}</Text>
      {invalidMappingNames.map((name) => (
        <Text key={name}>{name}</Text>
      ))}
    </Flex>
  ) : (
    descriptionText
  );

  return (
    <ClassNames>
      {(clsn) => (
        <Banner
          variant="warning"
          title={INVALID_MAPPING_TITLE}
          description={description}
          buttonProps={{
            children: isMultiple ? "Update mappings" : "Update mapping",
            onClick: () => window.open(mappingsLink, "_blank", "noopener,noreferrer"),
            variant: "neutral",
            icon: "external-link",
            iconPosition: "right",
            className: clsn.css({ flexShrink: 0, order: 4 }),
          }}
        />
      )}
    </ClassNames>
  );
};
